import { yupResolver } from '@hookform/resolvers/yup';
import { Input, Space, Typography } from 'antd';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import * as yup from 'yup';

import { useMaybeFetchServiceFee } from '../../../hooks/useMaybeFetchServiceFee';
import { useSearchParams } from '../../../hooks/useSearchParams';
import selectors from '../../../store/selectors';
import { formatOfferingAmountOption } from '../../../store/selectors/utils';
import { mobileOrHomePhoneRegexPattern } from '../../../utils/string';
import FormInputError from '../../Form/FormInputError';
import OfferingAmount from '../../Form/OfferingAmount';
import SubmitAddMassOffering from '../../Form/rhf/SubmitAddMassOffering/SubmitAddMassOffering';
import ServiceFee from '../../ServiceFee/ServiceFee';

import { devotionOfferingsFormNames } from './devotionOfferingsFormOfferingsFormNames';
import DevotionOfferingSelector from './DevotionOfferingSelector';
import DevotionOfferingDateSelector from './DevotionOfferingDateSelector';

const useDefaultValues = ({
  isServiceFeeEnabled,
  isServiceFeeSelectedCoveredDefault,
  isServiceFeeCoveredRequired,
  devotionsDictionary,
}) => {
  const urlSearchParams = useSearchParams();

  return useMemo(() => {
    const typeFromUrl = urlSearchParams.get('type');
    const predefinedType =
      (typeFromUrl && devotionsDictionary.find(item => item.title === typeFromUrl)?.id) ??
      devotionsDictionary[0].id;

    return {
      [devotionOfferingsFormNames.agreeOnRegulations]: false,
      [devotionOfferingsFormNames.email]: '',
      [devotionOfferingsFormNames.fullName]: '',
      [devotionOfferingsFormNames.offeringAmount]: null,
      [devotionOfferingsFormNames.totalAmountToPay]: null,
      [devotionOfferingsFormNames.offeringContent]: '',
      [devotionOfferingsFormNames.offeringType]: predefinedType,
      [devotionOfferingsFormNames.phone]: '',
      [devotionOfferingsFormNames.userCoversServiceFee]:
        isServiceFeeEnabled && (isServiceFeeCoveredRequired || isServiceFeeSelectedCoveredDefault),
    };
  }, []);
};

export const DevotionOfferingsForm = ({ onSubmit, loading, devotionsDictionary }) => {
  const isServiceFeeEnabled = useSelector(selectors.selectIsServiceFeeEnabled);
  const isServiceFeeSelectedCoveredDefault = useSelector(
    selectors.selectIsServiceFeeCoveredDefault,
  );
  const isServiceFeeCoveredRequired = useSelector(selectors.selectIsServiceFeeCoveredRequired);

  const massOfferingAmountDefault = useSelector(selectors.selectMassOfferingAmountOption);
  const regulationsUrl = useSelector(selectors.selectRegulationsUrl);
  // Get amount config for devotion types
  const devotionTypeAmounts = useSelector(state => state.accountOptions.devotionTypeAmount || {});
  const defaultValues = useDefaultValues({
    isServiceFeeEnabled,
    isServiceFeeSelectedCoveredDefault,
    isServiceFeeCoveredRequired,
    devotionsDictionary,
  });

  const formSchema = useMemo(() => {
    const devotionsDictionaryValues = devotionsDictionary.map(item => item.id);

    return yup.object().shape({
      [devotionOfferingsFormNames.agreeOnRegulations]: yup
        .bool()
        .oneOf([true], 'Musisz zgodzić się na regulamin, żeby zamówić intencję'),
      [devotionOfferingsFormNames.email]: yup.string().email().required('To pole jest wymagane'),
      [devotionOfferingsFormNames.devotionId]: yup.string().required('To pole jest wymagane'),
      [devotionOfferingsFormNames.fullName]: yup.string().required('To pole jest wymagane'),
      [devotionOfferingsFormNames.offeringAmount]: yup
        .number()
        .when(devotionOfferingsFormNames.offeringType, (offeringType, schema) => {
          // Get minimal value for the selected devotion type
          const devotionTypeAmount = offeringType ? devotionTypeAmounts[offeringType] : null;
          const minimalValue = devotionTypeAmount
            ? formatOfferingAmountOption(devotionTypeAmount).minimalValue
            : massOfferingAmountDefault?.minimalValue || 1;
          return schema.min(minimalValue, 'Podaj poprawną kwotę');
        })
        .required('To pole jest wymagane')
        .typeError('Podaj poprawną kwotę (tylko cyfry)'),
      [devotionOfferingsFormNames.offeringContent]: yup
        .string()
        .required('Uzupełnij treść intencji')
        .min(3, 'Uzupełnij treść intencji'),
      [devotionOfferingsFormNames.offeringType]: yup
        .string()
        .oneOf(devotionsDictionaryValues)
        .required('To pole jest wymagane'),
      [devotionOfferingsFormNames.phone]: yup.string().matches(
        // /(^((\+48)|(0048))?[ -]?[0-9]{3}[ -]?[0-9]{3}[ -]?[0-9]{3}$)|(^[0-9]{2}?[ -]?[0-9]{3}[ -]?[0-9]{2}[ -]?[0-9]{2}$)/
        new RegExp(mobileOrHomePhoneRegexPattern),
        {
          excludeEmptyString: true,
          message:
            'Podaj poprawny numer telefonu, np. w formacie 123 456 789 lub 22 123 12 12, lub +48 123 456 789',
        },
      ),
    });
  }, [massOfferingAmountDefault?.minimalValue, devotionsDictionary, devotionTypeAmounts]);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    control,
    setError,
    clearErrors,
    watch,
    setValue,
    trigger,
  } = useForm({
    resolver: yupResolver(formSchema),
    mode: 'onBlur',
    defaultValues,
  });

  const totalAmountToPay = watch(devotionOfferingsFormNames.totalAmountToPay);
  const offeringAmount = watch(devotionOfferingsFormNames.offeringAmount);
  const offeringType = watch(devotionOfferingsFormNames.offeringType);

  useEffect(() => {
    if (offeringType && offeringAmount != null) {
      trigger(devotionOfferingsFormNames.offeringAmount);
    }
  }, [offeringType, trigger, offeringAmount]);

  const massOfferingAmount = useMemo(() => {
    if (!offeringType) {
      return massOfferingAmountDefault;
    }
    const devotionTypeAmount = devotionTypeAmounts[offeringType];
    if (devotionTypeAmount) {
      return formatOfferingAmountOption(devotionTypeAmount);
    }
    return massOfferingAmountDefault;
  }, [offeringType, devotionTypeAmounts, massOfferingAmountDefault]);

  const { loading: serviceFeeLoading } = useMaybeFetchServiceFee({
    isServiceFeeEnabled,
    isServiceFeeSelectedCoveredDefault:
      isServiceFeeCoveredRequired || isServiceFeeSelectedCoveredDefault,
    offeringAmount,
    setTotalAmountToPay: value => setValue(devotionOfferingsFormNames.totalAmountToPay, value),
    userCoversServiceFee: watch(devotionOfferingsFormNames.userCoversServiceFee),
  });

  return (
    <div>
      <h2 className="el-h4 mb-4">Intencje na nabożeństwa</h2>

      {/* eslint-disable-next-line react/no-danger */}
      {/* <div dangerouslySetInnerHTML={{ __html: beforeFormText }} /> */}

      <form onSubmit={handleSubmit(onSubmit)}>
        <Space direction="vertical" className="d-flex mt-4" size="large">
          <div>
            <Controller
              control={control}
              name={devotionOfferingsFormNames.offeringType}
              render={({ field }) => (
                <DevotionOfferingSelector
                  disabled={loading}
                  fieldName={field.name}
                  fieldValue={field.value}
                  dictionary={devotionsDictionary}
                  onChange={newType => {
                    field.onChange(newType);
                  }}
                />
              )}
            />
          </div>

          <div>
            <Typography.Title level={4}>Termin odprawienia intencji</Typography.Title>
            <Controller
              control={control}
              name={devotionOfferingsFormNames.devotionId}
              render={({ field }) => (
                <DevotionOfferingDateSelector
                  disabled={loading}
                  fieldName={field.name}
                  fieldValue={field.value}
                  devotionType={watch(devotionOfferingsFormNames.offeringType)}
                  onChange={newType => {
                    field.onChange(newType);
                  }}
                />
              )}
            />
          </div>

          <div>
            <Typography.Title level={4}>Treść intencji</Typography.Title>
            <Typography.Text>
              Możemy kontaktować się z Tobą, jeśli intencja modlitwy nie będzie dla nas jasna.
            </Typography.Text>
            <Controller
              control={control}
              name={devotionOfferingsFormNames.offeringContent}
              render={({ field }) => {
                return (
                  <Input.TextArea
                    placeholder="Np. O potrzebne łaski dla ..., o zdrowie dla ..."
                    rows={3}
                    showCount
                    maxLength={300}
                    allowClear
                    disabled={loading}
                    name={field.name}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    value={field.value}
                  />
                );
              }}
            />
            {errors[devotionOfferingsFormNames.offeringContent] && (
              <FormInputError
                errorText={errors[devotionOfferingsFormNames.offeringContent].message}
              />
            )}
          </div>

          <div>
            <Controller
              control={control}
              name={devotionOfferingsFormNames.offeringAmount}
              render={({ field }) => {
                return (
                  <OfferingAmount
                    allowFreeOfferings={massOfferingAmount?.minimalValue === 0}
                    clearErrors={clearErrors}
                    loading={loading}
                    massOfferingAmount={massOfferingAmount}
                    minimalMassOfferingAmount={massOfferingAmount?.minimalValue}
                    name={field.name}
                    onBlur={field.onBlur}
                    onChange={field.onChange}
                    ref={field.ref}
                    setError={setError}
                    value={field.value}
                    showLabel
                  />
                );
              }}
            />
            {errors[devotionOfferingsFormNames.offeringAmount] && (
              <FormInputError
                errorText={errors[devotionOfferingsFormNames.offeringAmount].message}
              />
            )}
          </div>

          {isServiceFeeEnabled && (
            <ServiceFee
              inputFieldName={devotionOfferingsFormNames.userCoversServiceFee}
              isRequired={isServiceFeeCoveredRequired}
              offeringAmount={offeringAmount}
              register={register}
              serviceFeeLoading={serviceFeeLoading}
              totalAmountToPay={totalAmountToPay}
            />
          )}

          <div className="md-form input-group mt-0 mb-0">
            <input
              disabled={loading}
              className="form-control"
              autoComplete="name"
              type="text"
              id={devotionOfferingsFormNames.fullName}
              {...register(devotionOfferingsFormNames.fullName)}
            />
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label htmlFor={devotionOfferingsFormNames.fullName}>
              Imię i nazwisko zamawiającego
            </label>

            {errors[devotionOfferingsFormNames.fullName] && (
              <FormInputError errorText={errors[devotionOfferingsFormNames.fullName].message} />
            )}
          </div>

          <div className="md-form input-group mt-0 mb-0">
            <input
              disabled={loading}
              className="form-control"
              id={devotionOfferingsFormNames.email}
              type="email"
              autoComplete="email"
              {...register(devotionOfferingsFormNames.email)}
            />
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label htmlFor={devotionOfferingsFormNames.email}>E-mail zamawiającego</label>

            {errors[devotionOfferingsFormNames.email] && (
              <FormInputError errorText={errors[devotionOfferingsFormNames.email].message} />
            )}
          </div>

          <div className="md-form input-group mt-0 mb-0">
            <input
              disabled={loading}
              className="form-control"
              type="tel"
              autoComplete="tel"
              id={devotionOfferingsFormNames.phone}
              {...register(devotionOfferingsFormNames.phone)}
            />
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label htmlFor={devotionOfferingsFormNames.phone}>Numer telefonu zamawiającego</label>

            {errors[devotionOfferingsFormNames.phone] && (
              <FormInputError errorText={errors[devotionOfferingsFormNames.phone].message} />
            )}
          </div>

          <div className="md-form input-group mt-0 mb-0">
            <input
              disabled={loading}
              className="form-check-input"
              type="checkbox"
              value="1"
              id={devotionOfferingsFormNames.agreeOnRegulations}
              {...register(devotionOfferingsFormNames.agreeOnRegulations)}
            />
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label
              className="form-check-label w-100"
              htmlFor={devotionOfferingsFormNames.agreeOnRegulations}
              id={`label-${devotionOfferingsFormNames.agreeOnRegulations}`}
            >
              Akceptuję{' '}
              <a target="_blank" rel="noreferrer" href={regulationsUrl}>
                Regulamin
              </a>
            </label>

            {errors[devotionOfferingsFormNames.agreeOnRegulations] && (
              <FormInputError
                className="mt-2"
                errorText={errors[devotionOfferingsFormNames.agreeOnRegulations].message}
              />
            )}
          </div>
        </Space>

        <SubmitAddMassOffering
          name={devotionOfferingsFormNames.offeringAmount}
          disabled={!isValid}
          watch={watch}
          loading={loading}
          isOnlinePayment
          isPaymentExpiring
        />
      </form>
    </div>
  );
};

DevotionOfferingsForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  devotionsDictionary: PropTypes.array.isRequired,
  inboxDictionary: PropTypes.array.isRequired,
};
